<template>
  <div class="relative flex min-w-0 flex-col" :data-testid="testId">
    <!--
      `relative` torna a raiz o bloco de contenção do `sr-only` (position: absolute).
      Sem ele o texto escapa do `overflow-x-auto` do Kanban e alarga a página no mobile.
    -->
    <span
      class="font-mono font-bold leading-none tabular-nums"
      :class="[size === 'lg' ? 'text-2xl' : 'text-lg', toneClass]"
      :aria-hidden="srText ? 'true' : undefined"
      :data-testid="`${testId}-value`"
    >
      {{ value }}
    </span>
    <span v-if="srText" class="sr-only" :data-testid="`${testId}-sr`">{{ srText }}</span>
    <span v-if="due" class="mt-1 text-[11px] text-neutral50" :data-testid="`${testId}-due`">
      {{ due }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type {
  PriorityPaymentRow,
  PrioritySlaDto,
  PrioritySlaThresholds
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

import {
  PRIORITY_SLA_TEXT_CLASS,
  formatSlaCountdown,
  formatSlaDueText,
  formatSlaShortDuration,
  formatSlaStamp,
  isPendingSlaState,
  resolveLiveSla
} from '../slaFormat'

/**
 * Countdown grande do M3 com o prazo absoluto embaixo. Sem `aria-live`: um
 * leitor de tela anunciando a cada segundo torna a fila inutilizável. O leitor
 * lê o texto por extenso (`sr-only`, em minutos); o número visual (mm:ss) fica
 * `aria-hidden` — `aria-label` num `<span>` sem role não é lido de forma confiável.
 */
const props = withDefaults(
  defineProps<{
    sla: PrioritySlaDto | null
    row: Pick<PriorityPaymentRow, 'situation' | 'antecipeRequestedAt'>
    nowMs?: number | null
    thresholds?: PrioritySlaThresholds | null
    size?: 'md' | 'lg'
    testId?: string
  }>(),
  { nowMs: null, thresholds: null, size: 'md', testId: 'priority-sla-countdown' }
)

const live = computed(() =>
  props.sla ? resolveLiveSla(props.sla, props.nowMs, props.thresholds) : null
)

const value = computed(() => {
  const sla = props.sla
  const current = live.value
  if (!sla || !current) return '—'
  if (current.state === 'DONE') {
    return sla.completedAt ? formatSlaStamp(sla.completedAt, props.nowMs) : '—'
  }
  if (current.remainingSeconds === null) return '—'
  return formatSlaCountdown(current.remainingSeconds)
})

const srText = computed(() => {
  const current = live.value
  if (!current || current.remainingSeconds === null || !isPendingSlaState(current.state)) {
    return undefined
  }
  const duration = formatSlaShortDuration(current.remainingSeconds)
  return current.state === 'OVERDUE' ? `atrasado há ${duration}` : `faltam ${duration}`
})

const toneClass = computed(() =>
  live.value ? PRIORITY_SLA_TEXT_CLASS[live.value.state] : 'text-neutral50'
)

const due = computed(() =>
  props.sla && live.value ? formatSlaDueText(props.sla, live.value, props.row, props.nowMs) : null
)
</script>
